/**
 * HOW MUCH A PLAN PUTS INTO A GUARANTEED INCOME (owner, 21 Oct 2026 — the village review's decision 11,
 * applied to every module).
 *
 * The client is asked only whether they would consider one; the engine works out how much. It tests:
 *   1. up to a quarter of the money it invests (the share the client can change), never "all in";
 *   2. where it would lift the Age Pension, the amount that lifts it most — the amount that brings the
 *      assets Centrelink counts down to the full-pension limit, since only 60% of an annuity's price is
 *      counted (Social Security Guide 4.9.3.30), so each dollar moved takes 40 cents out of the count.
 * The second is offered only when it differs from the first by a meaningful amount and when, after it,
 * the counted assets would fall below the cut-off (otherwise no Age Pension is paid either way). Whether
 * the income test is what limits the pension is left to the projection: a candidate that does not lift
 * it simply does not lead.
 */
import { ASSET_TEST_LIMITS, PENSION_AGE } from './constants/ratesAndThresholds.ts';

/** The part of an annuity's price the assets test counts in its first years. */
export const ANNUITY_ASSET_COUNTED_SHARE = 0.6;
/** Smaller than this is not worth an annuity of its own. */
export const MIN_ANNUITY_AMOUNT = 25_000;

export interface PensionMaximisingInput {
    /** The assets Centrelink would count once the plan's other moves are made, before any annuity. */
    assessableAssets: number;
    isCouple: boolean;
    homeowner: boolean;
    /** The most that could go in (what the plan invests). */
    investable: number;
    /** The client's (and partner's) ages: the amount matters only if someone is of Age Pension age. */
    ages: Array<number | null | undefined>;
}

/** The annuity amount that lifts the Age Pension most, or null when no amount would lift it. */
export function pensionMaximisingAnnuity(i: PensionMaximisingInput): number | null {
    if (!i.ages.some((a) => a != null && a >= PENSION_AGE)) return null;
    const limits = (i.isCouple ? ASSET_TEST_LIMITS.couple : ASSET_TEST_LIMITS.single)[i.homeowner ? 'homeowner' : 'nonHomeowner'];
    const assets = Math.max(0, i.assessableAssets);
    const investable = Math.max(0, i.investable);
    if (assets <= limits.full) return null;
    const removedPerDollar = 1 - ANNUITY_ASSET_COUNTED_SHARE;
    const needed = (assets - limits.full) / removedPerDollar;
    const amount = Math.min(investable, needed);
    // Still at or above the cut-off afterwards: no pension either way.
    if (assets - amount * removedPerDollar >= limits.cutoff) return null;
    const rounded = Math.floor(amount / 1000) * 1000;
    return rounded >= MIN_ANNUITY_AMOUNT ? rounded : null;
}

/**
 * The amounts to test: the client's ceiling (a share of what is invested, or a dollar amount), and the
 * pension-maximising amount when it is below that ceiling and a different plan (more than 10% and $10,000
 * away). Never above the ceiling. Sorted, each at least the minimum.
 */
export function annuityTestAmounts(investable: number, capPct: number, pensionMax: number | null, capAmount?: number | null): number[] {
    const byShare = Math.round(Math.max(0, investable) * (Math.max(0, Math.min(100, capPct)) / 100));
    // The client's ceiling: a share, or a dollar amount they gave (owner, 21 Oct 2026, decision D3).
    const capped = capAmount != null && capAmount > 0 ? Math.min(Math.max(0, investable), Math.round(capAmount)) : byShare;
    const out: number[] = [];
    if (capped >= MIN_ANNUITY_AMOUNT) out.push(capped);
    // The amount that lifts the Age Pension most is held to the same ceiling: tested only when it is below it.
    const pm = pensionMax != null ? Math.min(pensionMax, capped) : null;
    if (pm != null && pm >= MIN_ANNUITY_AMOUNT && out.every((a) => Math.abs(a - pm) > Math.max(10_000, a * 0.1))) out.push(pm);
    return out.sort((a, b) => a - b);
}
